import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { UniversalPage, Card, NotificationCard, StatusBadge } from '@sighthost/ui';
import {
  adminReportsService,
  type TicketSlaStats,
} from '@/domains/reports/services/adminReportsService';

export default function SupportSlaPage() {
  const [data, setData] = useState<TicketSlaStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminReportsService
      .getTicketSlaStats()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <UniversalPage
      title="Support SLA"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Reports', href: '/admin/reports' },
        { label: 'Support SLA' },
      ]}
    >
      {error && <NotificationCard tone="danger" title="Could not load SLA stats" body={error} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Open tickets</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">{data?.total_open ?? '…'}</p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Closed (last 30d)</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">{data?.total_closed_30d ?? '…'}</p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Avg first response</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {data?.avg_first_response_hours !== null && data?.avg_first_response_hours !== undefined
              ? `${data.avg_first_response_hours} h`
              : '—'}
          </p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Avg resolution</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {data?.avg_resolution_hours !== null && data?.avg_resolution_hours !== undefined
              ? `${data.avg_resolution_hours} h`
              : '—'}
          </p>
        </Card>
      </div>

      <Card interactive={false}>
        <header className="flex items-center justify-between border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">SLA state of open tickets</h3>
          <Link to="/admin/support" className="text-xs font-medium text-primary-ink hover:underline">
            Open inbox →
          </Link>
        </header>
        <div className="grid grid-cols-3 gap-3 p-4">
          <div className="rounded border border-danger/20 bg-danger/5 p-4 text-center">
            <StatusBadge tone="failed">Breached</StatusBadge>
            <p className="mt-2 text-2xl font-semibold text-danger">{data?.breached ?? '…'}</p>
          </div>
          <div className="rounded border border-warning/20 bg-warning/5 p-4 text-center">
            <StatusBadge tone="expiring">Approaching</StatusBadge>
            <p className="mt-2 text-2xl font-semibold text-warning">{data?.approaching ?? '…'}</p>
          </div>
          <div className="rounded border border-success/20 bg-success/5 p-4 text-center">
            <StatusBadge tone="active">OK</StatusBadge>
            <p className="mt-2 text-2xl font-semibold text-success">{data?.ok ?? '…'}</p>
          </div>
        </div>
        <p className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
          Breached = open without first_response_at past department SLA hours. Approaching = past 75% of SLA.
        </p>
      </Card>
    </UniversalPage>
  );
}
